import React from "react";
import type { GridColDef, GridSlots, GridRenderCellParams, GridRowSelectionModel } from "@mui/x-data-grid";
import type { FileUploadItem } from "@Reducers/minimiseFileUpload";

/** One question of the upload form template, as this table renders a column for it. */
type TableTemplateQuestion = {
  _id?: string;
  questionValue?: string;
  fieldType?: string;
  isMandatory?: boolean;
  [key: string]: unknown;
};

/** One section of the upload form template. */
type TableTemplateSection = { questionFields?: TableTemplateQuestion[]; sectionHeading?: string };

/**
 * A row of this table: the upload item plus the flags the bulk-edit row and action column read.
 * `id` is narrowed to `number` because `FilesSlickGrid` — the only caller — stamps the 1-based row
 * number onto every row it passes (`UploadGridRow` / `BulkEditRow`), and the template callbacks below
 * index back into the upload list with `id - 1`. `FileUploadItem` declares it `number | string`.
 */
type TableRow = FileUploadItem & {
  id: number;
  isBulkEdit?: boolean;
  uploadAsNewFile?: boolean;
  uploadAsNewVersion?: boolean;
  uploadAsNewVersionRetainFields?: boolean;
};
import { DataGrid } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import { untranslated, useTranslation } from "@i18n";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { BsExclamationCircle } from "react-icons/bs";
import * as XLSX from "xlsx";
import { convertDate } from "@Services/luxonHelper";
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import Text from "@CubeComponents/html/Text/Text";
import { getNomenclatureFieldWidthPx } from "@Services/utils/useCustomFields";

/**
 * Custom-field answers are stored on the row under the template question's `_id`. `FileUploadItem`
 * is an interface, so it carries no index signature to read them through; spreading it produces an
 * object type TypeScript will index, without asserting the row into a shape it does not have.
 */
const readAnswer = (row: TableRow, questionId: string): unknown => {
  const answers: Record<string, unknown> = { ...row };
  return answers[questionId];
};

/**
 * Adapter for MUI's `baseCheckbox` slot. The slot types `label` as a `ReactNode` and its `ref` as the
 * underlying `<button>`, while `CheckBoxComponent` takes a `string` label and exposes an imperative
 * handle; bridging those two props is the whole difference, so bridge them rather than asserting the
 * component into the slot type.
 */
type GridBaseCheckboxProps = Omit<React.ComponentProps<GridSlots["baseCheckbox"]>, "ref">;

const GridBaseCheckbox = React.forwardRef<HTMLButtonElement, GridBaseCheckboxProps>(
  ({ label, slotProps, ...rest }, ref) => (
    <CheckBoxComponent
      {...rest}
      label={typeof label === "string" ? label : undefined}
      // The grid names the native-input slot `htmlInput`; MUI Material's `Checkbox` names it `input`.
      // Renaming it is what makes the grid's `aria-label`/`name` reach the rendered `<input>`.
      slotProps={slotProps?.htmlInput ? { input: slotProps.htmlInput } : undefined}
      ref={(handle) => {
        const element = handle?.element ?? null;
        if (typeof ref === "function") {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      }}
    />
  ),
);
GridBaseCheckbox.displayName = "GridBaseCheckbox";

export default function FilesTable({
  fileUploadList = [],
  selectedFileIds,
  setSelectedFileIds,
  gridNomenclatureTemplate,
  progressTemplate,
  gridNameTemplate,
  gridCustomFieldTemplate,
  template = [],
  gridUploadActionTemplate,
  fileNomenclature,
  isDirectImportFlow,
  gridLocationTemplate,
}: {
  /**
   * Optional, unlike the callbacks below: the sections come from `formTemplate.template`, which
   * `useFileManager` seeds without a `template` key at all, and `FilesSlickGrid` only turns that
   * `undefined` into `[]` through React 18 `defaultProps` — a mechanism React 19 ignores. The
   * guard below is what actually keeps this component working when no template was captured.
   */
  template?: TableTemplateSection[];
  /**
   * The rest are required, not optional: `FilesSlickGrid` is the only caller and always passes every
   * one of them, and this component dereferences them unconditionally. Marking them optional would
   * mean ~40 non-null assertions saying the same thing one site at a time.
   */
  fileNomenclature: { separator?: string; fields?: { _id?: string; name?: string }[] } | null;
  fileUploadList: TableRow[];
  gridNameTemplate: (row: TableRow, id: number) => React.ReactNode;
  progressTemplate: (row: TableRow, id: number) => React.ReactNode;
  gridUploadActionTemplate: (row: TableRow, id: number) => React.ReactNode;
  gridNomenclatureTemplate: (row: TableRow, id: number) => React.ReactNode;
  gridCustomFieldTemplate: (field: TableTemplateQuestion, id: number) => React.ReactNode;
  gridLocationTemplate?: (row: TableRow, id: number) => React.ReactNode;
  selectedFileIds?: number[];
  setSelectedFileIds?: (ids: number[]) => void;
  [key: string]: unknown;
}) {
  // `template` is defaulted to `[]` in the parameter list above (React 19 ignores `defaultProps`,
  // which is what `FilesSlickGrid` used to rely on). The remaining hole is real: a section can
  // carry no `questionFields`, whose `undefined` would otherwise survive `flatMap` and reach
  // `field._id` / `field.isMandatory` below.
  const questionFields = template.flatMap((section: TableTemplateSection) => section.questionFields ?? []);
  const { t } = useTranslation();

  const tailwindCenterClass = "flex justify-center items-center";

  const handleCellKeyDown = (event: React.KeyboardEvent) => {
    if (event.key == " ") {
      event.stopPropagation();
    }
  };

  const hasActionsColumn = fileUploadList.some(
    (file: TableRow) => !file.isFolder && !!gridUploadActionTemplate(file, file.id),
  );
  const hasLocationColumn = Boolean(isDirectImportFlow && gridLocationTemplate);
  const hasNomenclatureColumn =
    fileNomenclature?.fields?.length! > 0 && fileUploadList.some((file: TableRow) => !file.isFolder);
  // Both matched-container fields are declared `[key: string]: unknown` on `FileUploadItem` (they
  // carry the Excel import's own columns), so the label arrives as `unknown`; take it only when it
  // really is a string instead of letting a non-string flow on as one.
  const getRevisionLabel = (row: TableRow): string => {
    const label = row.matchedDirectImportContainer?.revisionLabel || row.matchedContainer?.revisionLabel;
    return typeof label === "string" ? label : "";
  };
  const hasRevisionColumn = fileUploadList.some((file: TableRow) => getRevisionLabel(file));
  const hasErrorsColumn = fileUploadList.some((file: TableRow) => !file.isFolder);

  const nomenclatureMinWidth = Math.min(
    640,
    Math.max(
      220,
      (fileNomenclature?.fields ?? []).reduce(
        (sum: number, f: { _id?: string; name?: string }) => sum + getNomenclatureFieldWidthPx(f) + 8,
        24,
      ),
    ),
  );

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 2,
      minWidth: 280,
      sortable: false,
      align: "left",
      filterable: false,
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params: GridRenderCellParams<TableRow>) =>
        params.row.isBulkEdit ? null : gridNameTemplate(params.row, params.row.id),
    },
    {
      field: "progress",
      headerName: "Progress",
      flex: 1,
      minWidth: 150,
      sortable: false,
      align: "left",
      filterable: false,
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params: GridRenderCellParams<TableRow>) =>
        params.row.isBulkEdit ? null : progressTemplate(params.row, params.row.id),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 240,
      maxWidth: 280,
      flex: 2,
      sortable: false,
      align: "left",
      filterable: false,
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params: GridRenderCellParams<TableRow>) => (
        <div onKeyDown={handleCellKeyDown} className="flex justify-start items-center w-full h-full px-2">
          {gridUploadActionTemplate(params.row, params.row.id)}
        </div>
      ),
    },
    {
      field: "uploadLocation",
      headerName: t("Location"),
      flex: 1,
      minWidth: 200,
      sortable: false,
      align: "left",
      filterable: false,
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params: GridRenderCellParams<TableRow>) =>
        params.row.isBulkEdit || !gridLocationTemplate ? null : gridLocationTemplate(params.row, params.row.id),
    },
    {
      field: "nomenclature",
      headerName: "Nomenclature",
      flex: 1,
      minWidth: nomenclatureMinWidth,
      sortable: false,
      align: "left",
      filterable: false,
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params: GridRenderCellParams<TableRow>) =>
        params.row.isBulkEdit ? null : gridNomenclatureTemplate(params.row, params.row.id),
    },
    {
      field: "revision",
      headerName: t("Revision"),
      flex: 1,
      minWidth: 140,
      sortable: false,
      filterable: false,
      align: "left",
      headerAlign: "left",
      cellClassName: tailwindCenterClass,
      headerClassName: tailwindCenterClass,
      renderCell: (params: GridRenderCellParams<TableRow>) =>
        params.row.isBulkEdit ? null : getRevisionLabel(params.row),
    },
  ];

  questionFields.forEach((field: TableTemplateQuestion) => {
    columns.push({
      field: field._id!,
      // `headerName` is a plain string — the grid also uses it for the header tooltip and the column
      // menu — so the mandatory asterisk is appended by the `.cube-mandatory-header` rule in `sx`
      // below rather than by smuggling a JSX element through this field.
      // `?? ""` keeps an unlabelled question's header blank as before; MUI falls back to `field`
      // (the question's `_id`) when `headerName` is nullish.
      headerName: field.questionValue ?? "",
      headerClassName: field.isMandatory ? "cube-mandatory-header" : undefined,
      flex: 1,
      minWidth: 260,
      sortable: false,
      filterable: false,
      resizable: true,
      align: "left",
      headerAlign: "left",
      renderCell: (params: GridRenderCellParams<TableRow>) => {
        const rawValue = readAnswer(params.row, field._id!);
        const fieldDisplayValue = Array.isArray(rawValue)
          ? rawValue.join(", ")
          : rawValue == null || rawValue === ""
            ? ""
            : String(rawValue);
        return (
          <div
            onKeyDown={handleCellKeyDown}
            role="gridcell"
            tabIndex={0}
            className="w-full"
            title={fieldDisplayValue || undefined}
          >
            {gridCustomFieldTemplate(field, params.row.id)}
          </div>
        );
      },
    });
  });

  columns.push({
    field: "errors",
    headerName: t("Errors"),
    flex: 1,
    minWidth: 300,
    maxWidth: 420,
    sortable: false,
    filterable: false,
    align: "left",
    headerAlign: "left",
    renderCell: (params: GridRenderCellParams<TableRow>) => {
      const errors: string[] = params.row.errors || [];
      if (!errors.length) return null;
      const errorText = errors.join(", ");
      return (
        <ToolTipComponent position="bottom" content={untranslated(errorText)}>
          <div className="flex items-center gap-2 min-w-0 overflow-hidden w-full">
            <div className="shrink-0 flex items-center">
              <BsExclamationCircle className="w-4 h-4 text-[#E05650]" />
            </div>
            <span className="text-[#E05650] text-sm truncate">{errorText}</span>
          </div>
        </ToolTipComponent>
      );
    },
  });

  const columnVisibilityModel = {
    actions: hasActionsColumn,
    uploadLocation: hasLocationColumn,
    nomenclature: hasNomenclatureColumn,
    revision: hasRevisionColumn,
    errors: hasErrorsColumn,
  };

  const getActionLabel = (row: TableRow): string => {
    if (row.uploadAsNewFile) return t("Upload as a New File");
    if (row.uploadAsNewVersion) return t("Upload as New version & override custom fields");
    if (row.uploadAsNewVersionRetainFields) return t("Upload as a new version & retain existing custom field data");
    return "";
  };

  const handleExcelExport = () => {
    const headers: string[] = ["Name", "Progress"];
    if (hasActionsColumn) headers.push(t("Actions"));
    if (isDirectImportFlow && gridLocationTemplate) headers.push(t("Location"));
    if (hasNomenclatureColumn) headers.push("Nomenclature");
    if (hasRevisionColumn) headers.push(t("Revision"));
    questionFields.forEach((field: TableTemplateQuestion) => {
      headers.push(field.questionValue + (field.isMandatory ? " *" : ""));
    });
    if (hasErrorsColumn) headers.push(t("Errors"));

    const rows = fileUploadList
      .filter((row: TableRow) => !row.isBulkEdit)
      .map((row: TableRow) => {
        // `progress` is declared `number | undefined`, so the non-number arm can only ever be blank.
        const rowData: unknown[] = [row.name || "", typeof row.progress === "number" ? `${row.progress}%` : ""];
        if (hasActionsColumn) rowData.push(getActionLabel(row));
        if (isDirectImportFlow && gridLocationTemplate) rowData.push(row.uploadLocation || "");
        if (hasNomenclatureColumn) rowData.push(row.nomenclature || "");
        if (hasRevisionColumn) rowData.push(getRevisionLabel(row));
        questionFields.forEach((field: TableTemplateQuestion) => {
          const val = readAnswer(row, field._id!);
          if (field.fieldType === "date-time" && val) {
            rowData.push(convertDate(Number(val), "NormalDate"));
          } else {
            rowData.push(Array.isArray(val) ? val.join(", ") : (val ?? ""));
          }
        });
        if (hasErrorsColumn) rowData.push((row.errors || []).join(", "));
        return rowData;
      });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Files");
    XLSX.writeFile(wb, "files_export.xlsx");
  };

  const handleRowSelectionChange = (selectionModel: GridRowSelectionModel) => {
    // `getRowId` below hands the grid this table's numeric row `id`, but `GridRowId` widens that to
    // `string | number`; keep the numbers rather than asserting the whole list into `number[]`.
    const idsArray = Array.from(selectionModel.ids).filter((id): id is number => typeof id === "number");
    setSelectedFileIds!(idsArray);
  };

  function renderFooter({ selectedCount, totalCount }: { selectedCount: number; totalCount: number }) {
    return (
      <div className="flex justify-between items-center px-4 py-2 text-sm text-gray-600 border-t border-[color:var(--stroke-primary)] w-full">
        <div>
          {selectedCount} {t("Items Selected")}
        </div>
        <div>
          {totalCount} {t("items")}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col">
      <div className="flex justify-end px-2 py-1 border-b border-[color:var(--stroke-primary)] shrink-0">
        <ToolTipComponent content={t("Export to Excel")}>
          <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleExcelExport}>
            <div className="flex gap-1 items-center">
              <IconComponent name="ExcelExportIcon" width="1rem" height="1rem" />
              <Text className="inline">{t("Export")}</Text>
            </div>
          </ButtonComponent>
        </ToolTipComponent>
      </div>
      <Box className="flex-1 min-h-0">
        <DataGrid
          rows={fileUploadList}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          checkboxSelection
          disableRowSelectionOnClick
          isRowSelectable={(params) => params.row?.id !== 0}
          getRowId={(row) => row.id}
          rowSelectionModel={{ type: "include", ids: new Set(selectedFileIds) }}
          onRowSelectionModelChange={handleRowSelectionChange}
          disableColumnMenu
          showCellVerticalBorder
          showColumnVerticalBorder
          rowHeight={80}
          sx={{
            // Match SlickGrid1 look (cubeComponent/html/SlickGrid1/index.css):
            // borders use --stroke-primary (#C6D8EC), near-white header,
            // 0.875rem text in --text-primary, --selection-primary selection.
            "--DataGrid-rowBorderColor": "var(--stroke-primary)",
            border: "1px solid var(--stroke-primary)",
            color: "var(--text-primary)",
            fontFamily: '"Source Sans Pro", sans-serif',
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#fafcff",
              color: "var(--text-primary)",
              justifyContent: "flex-start",
              alignItems: "center",
              display: "flex",
              textAlign: "left",
              padding: "0.5rem 0.75rem",
            },
            // Mandatory custom fields (see `headerClassName` above); keeps `headerName` a string.
            "& .cube-mandatory-header .MuiDataGrid-columnHeaderTitle::after": {
              content: '" *"',
              color: "#ef4444",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "600 !important",
              fontSize: "0.875rem",
              justifyContent: "flex-start",
              textAlign: "left",
              width: "100%",
            },
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              color: "var(--text-primary)",
              fontSize: "0.875rem",
              lineHeight: "1.3rem !important",
              padding: "0.5rem 0.75rem !important",
              overflow: "hidden",
            },
            "& .MuiFormControl-root": { height: "auto !important" },
            "& .MuiDataGrid-row": {
              alignItems: "center",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#e8edf5",
            },
            "& .MuiCheckbox-root": { margin: "auto" },
            "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover": {
              backgroundColor: "var(--selection-primary) !important",
            },
          }}
          localeText={{
            noRowsLabel: "No data to display.",
          }}
          slots={{
            baseCheckbox: GridBaseCheckbox,
            // Closed over rather than fed through `slotProps`: MUI types the footer slot as a plain
            // container component, so extra props would have to be laundered past that type.
            footer: () => renderFooter({ selectedCount: selectedFileIds!.length, totalCount: fileUploadList.length }),
          }}
        />
      </Box>
    </div>
  );
}
